import React, { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { useNavigate, useLocation } from 'react-router-dom'

const Help = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [markdownContent, setMarkdownContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [toc, setToc] = useState([])

  // Determine which app's help to load based on URL
  const getAppConfig = () => {
    const path = location.pathname
    if (path.includes('/pocket-portfolio')) {
      return {
        helpPath: '/pocket-portfolio/help.md',
        logoPath: '/pocket-portfolio/logo.png',
        appName: 'Pocket Portfolio',
        color: '#008B8B',
        backPath: '/pocket-portfolio'
      }
    } else if (path.includes('/photo-story')) {
      return {
        helpPath: '/photo-story/help.md',
        logoPath: '/photo-story/logo.png',
        appName: 'Photo Story',
        color: '#C05621',
        backPath: '/photo-story'
      }
    } else if (path.includes('/city-vs-city')) {
      return {
        helpPath: '/city-vs-city/help.md',
        logoPath: '/city-vs-city/logo.png',
        appName: 'City vs City',
        color: '#4A90E2',
        backPath: '/'
      }
    } else {
      // Default to QwikShift
      return {
        helpPath: '/qwikshift/help.md',
        logoPath: '/qwikshift/logo.png',
        appName: 'QwikShift',
        color: '#667eea',
        backPath: '/qwikshift'
      }
    }
  }

  const appConfig = getAppConfig()

  useEffect(() => {
    fetch(appConfig.helpPath)
      .then(response => response.text())
      .then(content => {
        setMarkdownContent(content)
        
        // Generate TOC from markdown content
        const lines = content.split('\n')
        const tocItems = []
        
        lines.forEach((line, index) => {
          if (line.startsWith('## ')) {
            const title = line.replace('## ', '').trim()
            const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-')
            tocItems.push({
              title,
              id,
              level: 2,
              lineNumber: index
            })
          } else if (line.startsWith('### ')) {
            const title = line.replace('### ', '').trim()
            const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-')
            tocItems.push({
              title,
              id,
              level: 3,
              lineNumber: index
            })
          }
        })
        
        setToc(tocItems)
        setLoading(false)
      })
      .catch(error => {
        console.error('Error loading help content:', error)
        setLoading(false)
      })
  }, [appConfig.helpPath])

  const markdownComponents = {
    img: ({ src, alt }) => (
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <img 
          src={src} 
          alt={alt}
          style={{ 
            maxWidth: '375px', 
            width: '100%',
            height: 'auto', 
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        />
      </div>
    ),
    h2: ({ children }) => {
      const id = children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-')
      return (
        <h2 
          id={id}
          style={{ 
            color: '#2c3e50',
            fontSize: '24px',
            fontWeight: '600',
            marginBottom: '20px',
            borderBottom: `2px solid ${appConfig.color}`,
            paddingBottom: '10px',
            marginTop: '40px',
            scrollMarginTop: '100px'
          }}
        >
          {children}
        </h2>
      )
    },
    h3: ({ children }) => {
      const id = children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-')
      return (
        <h3 
          id={id}
          style={{ 
            color: '#34495e',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '10px',
            marginTop: '25px',
            scrollMarginTop: '100px'
          }}
        >
          {children}
        </h3>
      )
    },
    em: ({ children }) => (
      <span style={{ fontStyle: 'italic', color: '#666', fontSize: '14px' }}>
        {children}
      </span>
    ),
    strong: ({ children }) => (
      <strong style={{ color: '#2c3e50' }}>
        {children}
      </strong>
    ),
    h1: ({ children }) => (
      <h1 style={{ 
        color: '#2c3e50', 
        fontSize: '32px', 
        fontWeight: '700', 
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        {children}
      </h1>
    ),
    
    p: ({ children }) => (
      <p style={{ 
        color: '#555',
        lineHeight: '1.6',
        marginBottom: '15px'
      }}>
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul style={{ 
        marginLeft: '20px',
        marginBottom: '15px'
      }}>
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol style={{ 
        marginLeft: '20px',
        marginBottom: '15px'
      }}>
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li style={{ 
        marginBottom: '8px',
        color: '#555'
      }}>
        {children}
      </li>
    )
  }

  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  if (loading) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center',
        color: '#666'
      }}>
        Loading help content...
      </div>
    )
  }

  return (
    <div style={{ 
      padding: window.innerWidth <= 768 ? '10px' : '20px', 
      maxWidth: '1200px', 
      margin: '0 auto',
      backgroundColor: '#f8f9fa',
      minHeight: 'calc(100vh - 64px)'
    }}>
      {/* Header with Back button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ 
          color: '#2c3e50', 
          fontSize: '24px', 
          fontWeight: '700', 
          margin: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <img src={appConfig.logoPath} alt={`${appConfig.appName} logo`} style={{ height: '32px', width: 'auto', borderRadius: '8px' }} />
          <span>Using {appConfig.appName}</span>
        </h1>
        <button
          onClick={() => navigate(appConfig.backPath)}
          style={{
            padding: '8px 16px',
            backgroundColor: appConfig.color,
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = appConfig.color
            e.target.style.opacity = '0.9'
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = appConfig.color
            e.target.style.opacity = '1'
          }}
        >
          Back
        </button>
      </div>

      {/* Table of Contents */}
      {toc.length > 0 && (
        <div style={{
          marginBottom: '40px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e1e5e9',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            color: '#2c3e50',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '15px',
            marginTop: '0'
          }}>
            📋 Table of Contents
          </h3>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0px'
          }}>
            {toc.map((item, index) => (
              <button
                key={index}
                onClick={() => scrollToSection(item.id)}
                style={{
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  color: appConfig.color,
                  fontSize: '14px',
                  fontWeight: item.level === 2 ? '600' : '400',
                  marginLeft: item.level === 3 ? '20px' : '0',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                  display: 'block',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  const hoverColor = appConfig.color + '20' // Add 20% opacity
                  e.target.style.backgroundColor = hoverColor
                  e.target.style.color = '#4a5568'
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent'
                  e.target.style.color = appConfig.color
                }}
              >
                {item.level === 2 ? '📖' : '📄'} {item.title}
              </button>
            ))}
          </div>
        </div>
      )}

      <ReactMarkdown 
        components={markdownComponents}
      >
        {markdownContent}
      </ReactMarkdown>
    </div>
  )
}

export default Help 